<script setup lang="ts">
import { type PropType } from 'vue';
import type { ReplacementSuggestion } from '@/api';
import { buildRedlineSegments, type RedlineSegment } from '@/services/playbooks/playbookRunDisplay';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import { gaiaRiskTint, gaiaStatus } from '../themes/tokens';

/**
 * A playbook's proposed redline for one field: the wording it would replace and
 * what it would put there, marked inline.
 *
 * Word-level rather than line-level. The review detail used to stack a whole
 * `- old` line above a whole `+ new` line, which made a two-word change look like
 * a rewritten clause. `buildRedlineSegments` — already used by the run grid and
 * the Word add-in — shares the common prefix and suffix, so only what actually
 * changed carries a mark.
 */

const props = defineProps({
  suggestions: {
    type: Array as PropType<ReplacementSuggestion[]>,
    required: true,
  },
});

const emit = defineEmits<{
  /** Jump the document pane to this suggestion's page and highlight its text. */
  select: [page: number, text: string];
}>();

function segments(suggestion: ReplacementSuggestion): RedlineSegment[] {
  return buildRedlineSegments(suggestion.string_to_replace, suggestion.replacement_string);
}

// Status tokens, not diff-specific hexes: a removal reads as the risk colour
// struck through, an addition as the success wash. The old view hardcoded
// GitHub's #ffebe9 / #e6ffed, which belong to no palette we own.
const removedColor = gaiaStatus.risk;
const addedColor = gaiaStatus.successText;
const addedSurface = gaiaRiskTint.successSurface;
</script>

<template>
  <n-flex vertical :size="12" :wrap="false">
    <n-flex
      v-for="(suggestion, index) in props.suggestions"
      :key="index"
      vertical
      :size="6"
      :wrap="false"
      class="suggestion"
      @click="emit('select', suggestion.page, suggestion.string_to_replace)"
    >
      <div class="diff">
        <span
          v-for="(segment, segmentIndex) in segments(suggestion)"
          :key="segmentIndex"
          :class="`segment segment--${segment.kind}`"
          >{{ segment.text }}</span
        >
      </div>
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-text v-if="suggestion.page" size="meta" tone="faint">
          Page {{ suggestion.page }}
        </gaia-text>
        <gaia-tooltip v-if="suggestion.reason">
          <template #trigger>
            <gaia-text size="meta" tone="subtle">Reason</gaia-text>
          </template>
          {{ suggestion.reason }}
        </gaia-tooltip>
      </n-flex>
    </n-flex>
  </n-flex>
</template>

<style scoped>
.suggestion {
  cursor: pointer;
}

/* A clause can be long and must not widen the pane it sits in. */
.diff {
  max-width: 100%;
  overflow-x: auto;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.segment--removed {
  color: v-bind(removedColor);
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
}

.segment--added {
  color: v-bind(addedColor);
  background: v-bind(addedSurface);
  border-radius: 3px;
  padding: 0 2px;
}
</style>
